import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import { DLOSimpleBreadcrumb } from '../../../../modules/dlo/components/DLOBreadcrumb';
import DLOModal from '../../../../modules/dlo/components/DLOModal';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector, useDispatch } from 'react-redux';
import store, { RootState } from '../../../../store';
import {
  setRestoreRequested,
  saveSnapshot,
  PerfectTruckAnalyticsSnapshot,
} from '../../../../store/slices/perfectTruckAnalyticsUiSlice';
import { getValidIconName } from '../../../AnalyticsHub/utils/reportsTabHelpers';
import { fetchPeriodWeeks } from '../../../../api/periodWeeksApi';
import KPIFilterDropdown, { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import DeliveryDateRangeFilter, { DeliveryDateRange } from '../../components/DeliveryDateRangeFilter';
import { FontFamily } from '../../../../constants/fonts';
import formatToLocalWithTZ from '../../constants/dateHelpers';
import { useDisabledPeriodWeekIds } from '../../../AnalyticsHub/hooks/useDisabledPeriodWeekIds';
import { useAdminLocations } from '../../../AnalyticsHub/hooks/useAdminLocations';
import ExecutiveOverview from './ExecutiveOverview';
import LocationSummary from './LocationSummary';
import {
  fetchAllRouteTypesWithStatus,
  fetchPerfectTruckKpi,
  fetchRefreshStatus,
  PerfectTruckKpiParams,
  PerfectTruckKpiResponse,
} from './perfectTruckKpiApi';

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_LINK = '/navigator-reports/perfect-truck-analytics';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeFilterValue = (value: unknown): string =>
  String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

type FilterKey = 'region' | 'market' | 'location' | 'year' | 'period' | 'week' | 'deliveryDate' | 'routeType';
type NavigatorReportTab = 'executive-overview' | 'location-summary';
type QuickSelectKey = 'wtd' | 'ptd' | 'qtd' | 'ytd' | 'lcw' | 'lcp';
type ChartHierarchyLevel = 'region' | 'market' | 'location';
type ChartTimeframeGrain = 'year' | 'period' | 'week' | 'day';

// Number of weeks in a period (fixed 4-4-4-4 / 13-period PepsiCo calendar).
const WEEKS_PER_PERIOD = 4;
// Quarters group periods as [1-3], [4-6], [7-9], [10-13].
const QUARTER_START_PERIODS = [1, 4, 7, 10];

const CHART_HIERARCHY_OPTIONS: { key: ChartHierarchyLevel; label: string }[] = [
  { key: 'region', label: 'Region / Unit' },
  { key: 'market', label: 'Market / POD' },
  { key: 'location', label: 'Location' },
];

const CHART_TIMEFRAME_OPTIONS: { key: ChartTimeframeGrain; label: string }[] = [
  { key: 'year', label: 'Year' },
  { key: 'period', label: 'Period' },
  { key: 'week', label: 'Week' },
  { key: 'day', label: 'Day' },
];

const QUICK_SELECT_OPTIONS: { key: QuickSelectKey; label: string }[] = [
  { key: 'wtd', label: 'Week-to-Date' },
  { key: 'ptd', label: 'Period-to-Date' },
  { key: 'qtd', label: 'Quarter-to-Date' },
  { key: 'ytd', label: 'Year-to-Date' },
  { key: 'lcw', label: 'Last Closed Week' },
  { key: 'lcp', label: 'Last Closed Period' },
];

const toISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isConsecutiveNumericSelection = (values: number[]): boolean => {
  const uniq = Array.from(new Set(values.filter((n) => Number.isFinite(n)))).sort((a, b) => a - b);
  if (uniq.length <= 1) return true;
  return uniq[uniq.length - 1] - uniq[0] + 1 === uniq.length;
};

// ─── Component ────────────────────────────────────────────────────────────────

const PerfectTruckAnalyticsDashboard: React.FC = () => {
  const { width } = useWindowDimensions();
  // ─── Report config from Redux ───────────────────────────────────────────
  const reportConfig = useSelector((state: RootState) =>
    state.reportingConfiguration.rows.find((row) => row.link === REPORT_LINK)
  );

  const iconName = getValidIconName(reportConfig?.icon || 'local-shipping');
  const title = reportConfig?.name || 'Perfect Truck Analytics';

  const dispatch = useDispatch();

  // Captured once on mount: the filter/tab snapshot to restore, but only when
  // the user arrived here via the Perfect Truck breadcrumb link (i.e. someone
  // explicitly asked to come back). Any other entry point (Analytics Hub
  // tile, direct nav, etc.) ignores any stale snapshot and starts fresh.
  const [restoreSnapshot] = useState<PerfectTruckAnalyticsSnapshot | null>(() => {
    const uiState = store.getState().perfectTruckAnalyticsUi;
    return uiState.restoreRequested ? uiState.snapshot : null;
  });

  useEffect(() => {
    // Consume the restore request immediately so it doesn't leak into a
    // later, unrelated mount of this dashboard.
    dispatch(setRestoreRequested(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Refresh status ─────────────────────────────────────────────────────
  const [lastRefreshedLabel, setLastRefreshedLabel] = useState<string>('');

  useEffect(() => {
    let isMounted = true;
    fetchRefreshStatus()
      .then((res) => {
        if (!isMounted) return;
        // Prefer the ISO timestamp if backend provides it so we can reliably
        // convert to the user's local timezone. Fall back to the label string
        // only when ISO is not available.
        const iso = (res as any)?.lastRefreshedAt ?? null;
        const label = (res as any)?.lastRefreshedLabel ?? null;
        setLastRefreshedLabel(formatToLocalWithTZ(iso ?? label ?? ''));
      })
      .catch(() => { /* silently ignore — badge simply won't show */ });
    return () => { isMounted = false; };
  }, []);

  // ─── KPI data ───────────────────────────────────────────────────────────
  const [kpiData, setKpiData] = useState<PerfectTruckKpiResponse | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  // ─── Error modal state ───────────────────────────────────────────────────
  const [apiErrorModal, setApiErrorModal] = useState<{ visible: boolean; title: string; message: string }>({
    visible: false,
    title: '',
    message: '',
  });

  const showApiError = (title: string, message: string) => {
    setApiErrorModal({ visible: true, title, message });
  };

  const dismissApiError = () => {
    setApiErrorModal((prev) => ({ ...prev, visible: false }));
  };

  // ─── Location summary error from Redux ──────────────────────────────────
  const locationSummaryError = useSelector((state: RootState) => state.perfectTruckLocationSummary?.error);

  useEffect(() => {
    if (locationSummaryError) {
      showApiError('Location Summary Failed', locationSummaryError);
    }
  }, [locationSummaryError]);

  // ─── Selected location from Redux header (pre-populate on mount) ──────────
  // Must be declared before any state that references it.
  const headerSelectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);

  // Computed once on mount — do NOT react to later header changes so the filter
  // state is not reset while the user is editing filters.
  const initialLocationFromHeader = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!headerSelectedLocation) return [];
    const name = String(headerSelectedLocation.displayText || headerSelectedLocation.name || '').trim();
    if (!name) return [];
    return [{ id: String(headerSelectedLocation.id || headerSelectedLocation.locationId || name), displayText: name }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const initialDefaultKpiParams = useMemo<PerfectTruckKpiParams>(() => {
    const params: PerfectTruckKpiParams = { year: String(currentYear) };
    if (initialLocationFromHeader.length > 0) {
      params.location = initialLocationFromHeader.map((l) => l.displayText).join(',');
    }
    return params;
  }, [currentYear, initialLocationFromHeader]);

  // Track whether we've already auto-populated from the header on initial load.
  // When restoring a snapshot, treat auto-population as already done so it
  // doesn't clobber the restored location selection.
  const hasAutoPopulatedRef = React.useRef(!!restoreSnapshot || initialLocationFromHeader.length > 0);

  // Tracks the last-applied filter params so both tabs share the same filters
  const [appliedFilterParams, setAppliedFilterParams] = useState<PerfectTruckKpiParams>(
    () => restoreSnapshot?.appliedFilterParams ?? initialDefaultKpiParams,
  );

  // Tracks the params baseline from the last *manual* Apply/Reset/initial load —
  // used only to drive the Apply button's enabled state. Quick-select applies
  // data immediately (updates appliedFilterParams above) but intentionally does
  // NOT move this baseline, so Apply stays enabled afterward (matches Analytics
  // Hub) letting the user still explicitly re-commit the quick-selected filters.
  const [manualBaselineParams, setManualBaselineParams] = useState<PerfectTruckKpiParams>(
    () => restoreSnapshot?.manualBaselineParams ?? {
      ...initialDefaultKpiParams,
      locationHierarchy: 'region',
      timeframe: 'period',
    },
  );

  // ─── Filter state ───────────────────────────────────────────────────────
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null);
  const [activeTab, setActiveTab] = useState<NavigatorReportTab>(
    () => restoreSnapshot?.activeTab ?? 'executive-overview',
  );
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);

  // Region
  const [selectedRegions, setSelectedRegions] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedRegions ?? [],
  );

  // Market
  const [selectedMarkets, setSelectedMarkets] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedMarkets ?? [],
  );

  // Location — pre-seeded with the header's selected location (or the restored snapshot)
  const [selectedLocations, setSelectedLocations] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedLocations ?? initialLocationFromHeader,
  );

  // Year — pre-seeded with current year (same as location)
  const [selectedYears, setSelectedYears] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedYears ?? [{ id: String(currentYear), displayText: String(currentYear) }],
  );

  // Period
  const [selectedPeriods, setSelectedPeriods] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedPeriods ?? [],
  );

  // Week
  const [selectedWeeks, setSelectedWeeks] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedWeeks ?? [],
  );

  // Delivery Date (range)
  const [deliveryDateRange, setDeliveryDateRange] = useState<DeliveryDateRange>(
    () => restoreSnapshot?.deliveryDateRange ?? { start: null, end: null },
  );

  // Time Frame quick-select (Week-to-Date / Period-to-Date / etc.) — one active at a time.
  // Manually adjusting Year, Period, Week or Delivery Date deselects the active quick-select.
  const [activeQuickSelect, setActiveQuickSelect] = useState<QuickSelectKey | null>(
    () => (restoreSnapshot?.activeQuickSelect as QuickSelectKey | null) ?? null,
  );

  // Route Type
  const [routeTypeOptions, setRouteTypeOptions] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedRouteTypes, setSelectedRouteTypes] = useState<KPIFilterDropdownItem[]>(
    () => restoreSnapshot?.selectedRouteTypes ?? [],
  );

  // ─── Chart Configuration (Executive Overview tab only) ──────────────────
  const [isChartConfigExpanded, setIsChartConfigExpanded] = useState(false);
  const [chartHierarchyLevel, setChartHierarchyLevel] = useState<ChartHierarchyLevel>('region');
  const [chartTimeframeGrain, setChartTimeframeGrain] = useState<ChartTimeframeGrain>('period');
  const initialKpiLoadCompleteRef = React.useRef(false);

  // ─── Derived IDs for cascading ──────────────────────────────────────────
  const selectedRegionIds: string[] = useMemo(() => {
    if (selectedRegions.length === 0) return ['all'];
    return selectedRegions.map((r) => normalizeFilterValue(r.id));
  }, [selectedRegions]);

  const selectedMarketIds: string[] = useMemo(() => {
    if (selectedMarkets.length === 0) return ['all'];
    return selectedMarkets.map((m) => normalizeFilterValue(m.id));
  }, [selectedMarkets]);

  // ─── Locations from cached Admin Locations flow ──────────────────────────
  const { locations: adminLocations } = useAdminLocations();

  // ─── Region options derived from Redux locations ─────────────────────────
  const regionOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];
    adminLocations.forEach((loc) => {
      const r = loc.region?.trim();
      if (r && !seen.has(r)) {
        seen.add(r);
        options.push({ id: r, displayText: r });
      }
    });
    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations]);

  // ─── Market options derived from Redux locations (filtered by selected regions) ─
  const marketOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];
    adminLocations
      .filter((loc) =>
        selectedRegions.length === 0 ||
        selectedRegions.some(
          (r) => normalizeFilterValue(r.id) === normalizeFilterValue(loc.region),
        ),
      )
      .forEach((loc) => {
        const m = loc.market?.trim();
        if (m && !seen.has(m)) {
          seen.add(m);
          options.push({ id: m, displayText: m });
        }
      });
    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations, selectedRegions]);

  // ─── Location options from Redux (filtered by region + market) ──────────
  const locationOptions: KPIFilterDropdownItem[] = useMemo(
    () =>
      adminLocations
        .filter((loc) => {
          const matchesRegion =
            selectedRegionIds.includes('all') ||
            selectedRegionIds.includes(normalizeFilterValue(loc.region));
          const matchesMarket =
            selectedMarketIds.includes('all') ||
            selectedMarketIds.includes(normalizeFilterValue(loc.market));
          return matchesRegion && matchesMarket;
        })
        .map((loc) => ({ id: loc.locId, displayText: loc.locName }))
        .sort((a, b) => a.displayText.localeCompare(b.displayText)),
    [adminLocations, selectedRegionIds, selectedMarketIds]
  );

  // When locationOptions become available, ensure any existing `selectedLocations`
  // are valid canonical items. This mirrors Analytics Hub's validation: keep
  // valid items, map non-canonical selections to canonical options when
  // possible, otherwise clear the selection (means 'All'). This prevents the
  // dropdown trigger from showing a label that doesn't match any checkboxes.
  useEffect(() => {
    if (!locationOptions || locationOptions.length === 0) return;

    setSelectedLocations((prev) => {
      if (!prev || prev.length === 0) return prev;

      // Keep items that match by id
      const validItems = prev.filter((selectedLoc) =>
        locationOptions.some((opt) => String(opt.id) === String(selectedLoc.id))
      );
      if (validItems.length > 0) return validItems;

      // Try to find a canonical option by displayText or by a 'locationId' field
      const sel = prev[0];
      const match = locationOptions.find((opt) =>
        String(opt.id) === String((sel as any).locationId) ||
        normalizeFilterValue(opt.displayText) === normalizeFilterValue(sel.displayText) ||
        normalizeFilterValue(opt.displayText) === normalizeFilterValue((sel as any).name || '')
      );

      if (match) return [match];

      // No valid mapping — clear selection to represent 'All'
      return [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOptions]);

  // Ensure any pre-seeded selected location (from header) is normalized to
  // one of the `locationOptions` items so the KPIFilterDropdown recognizes
  // it as selected. This runs once when the location options become
  // available (adminLocations fetch) and will replace the initial
  // placeholder item with the canonical option object when a match exists.
  useEffect(() => {
    if (hasAutoPopulatedRef.current) return; // only auto-populate once
    if (!headerSelectedLocation) return;
    if (!locationOptions || locationOptions.length === 0) return;

    const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
    const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';

    const matchingOption = locationOptions.find(
      (opt) => String(opt.id) === locId || opt.displayText === locName || normalizeFilterValue(opt.displayText) === normalizeFilterValue(locName)
    );

    if (matchingOption) {
      setSelectedLocations([matchingOption]);
      hasAutoPopulatedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerSelectedLocation, locationOptions]);

  // Whenever the header-selected location changes (user changed location
  // via DLOHeader), refresh the dashboard filters and reload KPI data so
  // the change is immediately reflected in both tabs.
  useEffect(() => {
    if (!headerSelectedLocation) return;

    // Normalize to canonical option if possible
    const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
    const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';
    const matchingOption = (locationOptions || []).find(
      (opt) => String(opt.id) === locId || normalizeFilterValue(opt.displayText) === normalizeFilterValue(locName)
    );

    const newSelectedLocations = matchingOption ? [matchingOption] : (locId || locName ? [{ id: locId || String(locName), displayText: String(locName) }] : []);

    // Apply selection to UI
    setSelectedLocations(newSelectedLocations);

    // Build filter params with new location and reload KPI
    const locationParam = newSelectedLocations.length > 0 ? newSelectedLocations.map(l => l.displayText).join(',') : undefined;
    const newApplied: PerfectTruckKpiParams = {
      ...appliedFilterParams,
      ...(locationParam ? { location: locationParam } : {}),
    };

    setAppliedFilterParams(newApplied);
    setManualBaselineParams({
      ...newApplied,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    });

    loadKpi({
      ...newApplied,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerSelectedLocation]);

  // ─── Year options (2025 up to current year, most recent first) ─────────
  const FIRST_ANALYTICS_YEAR = 2025;
  const yearOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const count = Math.max(1, currentYear - FIRST_ANALYTICS_YEAR + 1);
    return Array.from({ length: count }, (_, i) => ({
      id: String(currentYear - i),
      displayText: String(currentYear - i),
    }));
  }, [currentYear]);

  // ─── Period & Week: fetched directly from /period-weeks API ─────────────────
  // Matches Analytics Hub behavior: when no year is selected, selectedYearNumbers
  // is empty so Period falls back to its static, unconstrained P1–P13 list while
  // Week has no data to derive from and shows empty ("No items found") — exactly
  // like Analytics Hub's KPI Overview tab.
  const selectedYearNumbers = useMemo(
    () => selectedYears.map((y) => Number(y.id)).filter((n) => !isNaN(n)),
    [selectedYears],
  );
  const [rawPeriodWeeksData, setRawPeriodWeeksData] = useState<any[]>([]);

  useEffect(() => {
    if (selectedYearNumbers.length === 0) {
      setRawPeriodWeeksData([]);
      return;
    }
    let isMounted = true;
    fetchPeriodWeeks(selectedYearNumbers)
      .then((res) => { if (isMounted) setRawPeriodWeeksData(res.data ?? []); })
      .catch(() => { if (isMounted) setRawPeriodWeeksData([]); });
    return () => { isMounted = false; };
  }, [selectedYearNumbers]);

  // Period options — fallback to static P1–P13 while loading
  const periodOptions: KPIFilterDropdownItem[] = useMemo(() => {
    if (rawPeriodWeeksData.length === 0) {
      return Array.from({ length: 13 }, (_, i) => ({ id: `P${i + 1}`, displayText: `P${i + 1}` }));
    }
    const periodSet = new Set<number>();
    rawPeriodWeeksData.forEach((yd: any) => {
      (yd.periods ?? []).forEach((pd: any) => {
        if (pd.period != null) periodSet.add(Number(pd.period));
      });
    });
    return Array.from(periodSet)
      .sort((a, b) => a - b)
      .map((p) => ({ id: `P${p}`, displayText: `P${p}` }));
  }, [rawPeriodWeeksData]);

  // Week options — format W{weekInYear} (P{period}W{weekInPeriod}),
  // filtered by selected periods. Handles both number and object week formats.
  // NOTE: unlike Period, Week has no static fallback list — Analytics Hub only
  // ever shows weeks derived from the API response (which requires a year to
  // be selected). With no year selected, weekOptions is empty ("No items found"),
  // matching Analytics Hub's KPI Overview behavior exactly.
  const weekOptions: KPIFilterDropdownItem[] = useMemo(() => {
    if (rawPeriodWeeksData.length === 0) {
      return [];
    }
    const activePeriodIds = selectedPeriods.map((p) => String(p.id)); // e.g. ["P1", "P3"]
    const items: KPIFilterDropdownItem[] = [];

    rawPeriodWeeksData.forEach((yd: any) => {
      (yd.periods ?? []).forEach((pd: any) => {
        const periodNum = Number(pd.period);
        if (activePeriodIds.length > 0 && !activePeriodIds.includes(`P${periodNum}`)) return;
        (pd.weeks ?? []).forEach((week: any, idx: number) => {
          // Handle both number weeks (week-in-year) and object weeks
          const weekInYear = typeof week === 'number'
            ? week
            : Number(week?.weekInYear ?? week?.week ?? idx + 1);
          const weekInPeriod = idx + 1;
          // Use a period-aware id so the same week-in-year appearing in
          // multiple periods does not produce duplicate ids that collide
          // in the dropdown (which would cause multiple items to be
          // selected at once). Keep the displayText showing the week-in-year.
          items.push({
            id: `P${periodNum}W${weekInPeriod}`,
            displayText: `W${weekInYear} (P${periodNum}W${weekInPeriod})`,
            weekInYearNum: weekInYear,
          });
        });
      });
    });

    // Deduplicate by id (period+weekInPeriod). When multiple years are
    // selected rawPeriodWeeksData will contain entries per year which can
    // produce repeated period/week pairs — keep a single entry per period
    // and week so the dropdown doesn't show duplicates.
    const uniqueMap = new Map<string, KPIFilterDropdownItem>();
    items.forEach((it) => {
      const key = String(it.id);
      if (!uniqueMap.has(key)) uniqueMap.set(key, it);
    });
    const uniqueItems = Array.from(uniqueMap.values());

    return uniqueItems.sort((a, b) => {
      const aNum = Number((a as any).weekInYearNum) || 0;
      const bNum = Number((b as any).weekInYearNum) || 0;
      return aNum - bNum;
    });
  }, [rawPeriodWeeksData, selectedPeriods]);

  const currentPeriodWeek = useSelector((state: RootState) => state.appData?.periodWeek?.data);

  const getScopedDateRangeFromPeriodWeeks = useCallback(
    (
      yearNumbers: number[],
      periodNumbers: number[] = [],
      weekEntries: { period: number; weekInPeriod: number }[] = [],
    ): { start: string; end: string } | null => {
      if (rawPeriodWeeksData.length === 0 || yearNumbers.length === 0) return null;

      const yearSet = new Set(yearNumbers.filter((n) => Number.isFinite(n)));
      const periodSet = periodNumbers.length > 0 ? new Set(periodNumbers.filter((n) => Number.isFinite(n))) : null;
      const weekSet =
        weekEntries.length > 0
          ? new Set(weekEntries.map(({ period, weekInPeriod }) => `${period}:${weekInPeriod}`))
          : null;

      const dates: string[] = [];

      rawPeriodWeeksData.forEach((yd: any) => {
        if (yearSet.size > 0 && !yearSet.has(Number(yd.year))) return;

        (yd.periods ?? []).forEach((pd: any) => {
          const periodNum = Number(pd.period);
          if (periodSet && !periodSet.has(periodNum)) return;

          (pd.weeks ?? []).forEach((weekObj: any) => {
            const weekInPeriod = Number(weekObj.week ?? weekObj.weekInPeriod ?? 0);
            if (weekSet && !weekSet.has(`${periodNum}:${weekInPeriod}`)) return;

            (weekObj.days ?? []).forEach((day: any) => {
              if (day?.date) dates.push(day.date);
            });
          });
        });
      });

      if (dates.length === 0) return null;
      dates.sort();
      return { start: dates[0], end: dates[dates.length - 1] };
    },
    [rawPeriodWeeksData],
  );

  const constrainedDeliveryDateScope = useMemo<{ start: string; end: string } | null>(() => {
    if (selectedYears.length !== 1) return null;
    const selectedYear = Number(selectedYears[0]?.id);
    if (!Number.isFinite(selectedYear)) return null;
    const selectedPeriodNumbers = selectedPeriods
      .map((p) => Number(String(p.id).replace(/^P/i, '')))
      .filter((n) => Number.isFinite(n));
    const selectedWeekEntries = selectedWeeks
      .map((w) => {
        const idMatch = String(w.id).match(/^P(\d+)W(\d+)$/i);
        const textMatch = String(w.displayText).match(/\(P(\d+)W(\d+)\)/i);
        const periodNum = Number(idMatch?.[1] ?? textMatch?.[1]);
        const weekInPeriodNum = Number(idMatch?.[2] ?? textMatch?.[2]);
        if (!Number.isFinite(periodNum) || !Number.isFinite(weekInPeriodNum)) return null;
        return { period: periodNum, weekInPeriod: weekInPeriodNum };
      })
      .filter((entry): entry is { period: number; weekInPeriod: number } => entry !== null);

    return getScopedDateRangeFromPeriodWeeks([selectedYear], selectedPeriodNumbers, selectedWeekEntries);
  }, [selectedYears, selectedWeeks, selectedPeriods, getScopedDateRangeFromPeriodWeeks]);

  const hasDiscontinuousTimeframeSelection = useMemo(() => {
    const selectedPeriodNumbers = selectedPeriods
      .map((p) => Number(String(p.id).replace(/^P/i, '')))
      .filter((n) => Number.isFinite(n));

    const selectedWeekSequence = selectedWeeks
      .map((w) => {
        const idMatch = String(w.id).match(/^P(\d+)W(\d+)$/i);
        const periodNum = Number(idMatch?.[1]);
        const weekInPeriodNum = Number(idMatch?.[2]);
        if (!Number.isFinite(periodNum) || !Number.isFinite(weekInPeriodNum)) return NaN;
        return (periodNum - 1) * WEEKS_PER_PERIOD + weekInPeriodNum;
      })
      .filter((n) => Number.isFinite(n));

    // If period/week granularity is selected, year must be singular.
    if ((selectedPeriodNumbers.length > 0 || selectedWeekSequence.length > 0) && selectedYears.length !== 1) {
      return true;
    }

    if (!isConsecutiveNumericSelection(selectedPeriodNumbers)) return true;
    if (!isConsecutiveNumericSelection(selectedWeekSequence)) return true;
    return false;
  }, [selectedPeriods, selectedWeeks, selectedYears]);

  const deliveryDateDisabledReason = hasDiscontinuousTimeframeSelection
    ? 'Select a consecutive timeframe.'
    : null;

  useEffect(() => {
    if (!hasDiscontinuousTimeframeSelection) return;
    if (!deliveryDateRange.start && !deliveryDateRange.end) return;
    setDeliveryDateRange({ start: null, end: null });
  }, [hasDiscontinuousTimeframeSelection, deliveryDateRange.start, deliveryDateRange.end]);

  useEffect(() => {
    // Year/Period/Week -> Delivery Date sync: only when period/week filters are
    // selected and form a consecutive range.
    if (activeQuickSelect) return;
    if (hasDiscontinuousTimeframeSelection) return;
    if (selectedPeriods.length === 0 && selectedWeeks.length === 0) return;
    if (!constrainedDeliveryDateScope) return;

    const { start, end } = constrainedDeliveryDateScope;
    if (deliveryDateRange.start === start && deliveryDateRange.end === end) return;
    setDeliveryDateRange({ start, end });
  }, [
    activeQuickSelect,
    hasDiscontinuousTimeframeSelection,
    constrainedDeliveryDateScope,
    selectedPeriods,
    selectedWeeks,
  ]);

  const isDeliveryDateOutsideConstrainedScope = useMemo(() => {
    if (!constrainedDeliveryDateScope) return false;
    const { start, end } = constrainedDeliveryDateScope;
    const datesToValidate = [deliveryDateRange.start, deliveryDateRange.end].filter(Boolean) as string[];
    if (datesToValidate.length === 0) return false;
    return datesToValidate.some((d) => d < start || d > end);
  }, [constrainedDeliveryDateScope, deliveryDateRange]);

  const { disabledPeriodIds } = useDisabledPeriodWeekIds({
    currentPeriodWeek,
    selectedYearNumbers,
    periodOptions,
    weekOptions,
  });

  // Override disabledWeekIds: the generic hook strips non-digits from the id
  // (e.g. 'P6W1' → 61) which miscompares against the real weekInYear number
  // and incorrectly disables weeks beyond ~P2W4.  We use the weekInYearNum
  // extra property that is attached to each week option instead.
  const disabledWeekIds = useMemo(() => {
    if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return new Set<string>();
    if (selectedYearNumbers.length !== 1 || selectedYearNumbers[0] !== currentPeriodWeek.year) return new Set<string>();
    const currentWeekInYear = (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week;
    const disabled = new Set<string>();
    for (const opt of weekOptions) {
      const weekInYearNum = Number((opt as any).weekInYearNum);
      if (Number.isFinite(weekInYearNum) && weekInYearNum > currentWeekInYear) {
        disabled.add(String(opt.id));
      }
    }
    return disabled;
  }, [currentPeriodWeek, selectedYearNumbers, weekOptions]);

  const isMobileIos = Platform.OS === 'ios' && width < 768;

  // Count of actively-applied filter dimensions — drives the badge on the mobile Filter button
  const mobileActiveFilterCount = useMemo(() => {
    let count = 0;
    if (selectedRegions.length > 0) count++;
    if (selectedMarkets.length > 0) count++;
    if (selectedLocations.length > 0) count++;
    if (selectedYears.length > 0) count++;
    if (selectedPeriods.length > 0) count++;
    if (selectedWeeks.length > 0) count++;
    if (deliveryDateRange.start || deliveryDateRange.end) count++;
    if (selectedRouteTypes.length > 0) count++;
    return count;
  }, [selectedRegions, selectedMarkets, selectedLocations, selectedYears, selectedPeriods, selectedWeeks, deliveryDateRange, selectedRouteTypes]);

  // ─── Cascading: invalidate downstream when upstream changes ─────────────

  // When region changes → clear market & location
  const handleRegionChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedRegions(value);
    setSelectedMarkets([]);
    setSelectedLocations([]);
  };

  // When market changes → clear location
  const handleMarketChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedMarkets(value);
    setSelectedLocations([]);
  };

  // When year changes → clear period & week
  const handleYearChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedYears(value);
    setSelectedPeriods([]);
    setSelectedWeeks([]);
    setActiveQuickSelect(null);
  };

  // When period changes → clear week
  const handlePeriodChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedPeriods(value);
    setSelectedWeeks([]);
    setActiveQuickSelect(null);
  };

  // Manual week change — deselects any active quick-select (manual filters override)
  const handleWeekChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedWeeks(value);
    setActiveQuickSelect(null);
  };

  // Manual delivery date change — deselects any active quick-select (manual filters override)
  const handleDeliveryDateChange = (value: DeliveryDateRange) => {
    if (hasDiscontinuousTimeframeSelection) return;
    const clamp = (iso: string | null): string | null => {
      if (!iso || !constrainedDeliveryDateScope) return iso;
      if (iso < constrainedDeliveryDateScope.start) return constrainedDeliveryDateScope.start;
      if (iso > constrainedDeliveryDateScope.end) return constrainedDeliveryDateScope.end;
      return iso;
    };
    setDeliveryDateRange({
      start: clamp(value.start),
      end: clamp(value.end),
    });
    setActiveQuickSelect(null);
  };

  // Reset all filters — restore initial location + current year and reload KPI
  const handleResetFilters = () => {
    const resetYear: KPIFilterDropdownItem = { id: String(currentYear), displayText: String(currentYear) };
    setSelectedRegions([]);
    setSelectedMarkets([]);
    setSelectedLocations(initialLocationFromHeader);
    setSelectedYears([resetYear]);
    setSelectedPeriods([]);
    setSelectedWeeks([]);
    setSelectedRouteTypes([]);
    setDeliveryDateRange({ start: null, end: null });
    setActiveQuickSelect(null);
    const resetParams: PerfectTruckKpiParams = { year: String(currentYear) };
    if (initialLocationFromHeader.length > 0) {
      resetParams.location = initialLocationFromHeader.map((l) => l.displayText).join(',');
    }
    setAppliedFilterParams(resetParams);
    setManualBaselineParams({
      ...resetParams,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    });
    loadKpi({
      ...resetParams,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    });
  };

  // Compute current filter params from selection (canonicalized)
  // Geo priority: explicit locations > market filter > region filter.
  // Region and market are sent directly as params so the backend filters
  // by those columns in the pre-calc table rather than receiving hundreds
  // of individual location names.
  // 
  // IMPORTANT: Backend expects NO location parameter for "all locations".
  // Never send all location names (causes 400 URL too long error).
  const currentFilterParams = useMemo<PerfectTruckKpiParams>(() => {
    const params: PerfectTruckKpiParams = {};

    // Safeguard: if somehow selectedLocations includes too many items
    // (e.g., user selected "All"), omit location param to avoid URL length errors
    if (selectedLocations.length > 0 && selectedLocations.length < locationOptions.length) {
      // Explicit location selection (but not "all") — always takes precedence
      params.location = selectedLocations.map((l) => l.displayText).join(',');
    } else if (selectedMarkets.length > 0) {
      // Market selected (with or without region) — send market param.
      // Also send region if selected so the backend can scope to both.
      params.market = selectedMarkets.map((m) => String(m.id)).join(',');
      if (selectedRegions.length > 0) {
        params.region = selectedRegions.map((r) => String(r.id)).join(',');
      }
    } else if (selectedRegions.length > 0) {
      // Only region selected — send region param
      params.region = selectedRegions.map((r) => String(r.id)).join(',');
    }
    // If none of above, no geo params = backend returns all locations

    // Year
    if (selectedYears.length > 0) params.year = selectedYears.map((y) => String(y.id)).join(',');

    // Weeks & Periods
    if (selectedWeeks.length > 0) {
      const periodsFromWeeks = new Set<string>();
      const weekInPeriodValues: string[] = [];
      selectedWeeks.forEach((w) => {
        const match = String(w.displayText).match(/\(P(\d+)W(\d+)\)/);
        if (match) {
          periodsFromWeeks.add(match[1]);
          weekInPeriodValues.push(match[2]);
        }
      });
      if (weekInPeriodValues.length > 0) {
        params.week = weekInPeriodValues.join(',');
        const explicitPeriods = selectedPeriods.map((p) => String(p.id).replace(/^P/i, ''));
        const allPeriods = Array.from(new Set([...explicitPeriods, ...periodsFromWeeks]));
        if (allPeriods.length > 0) params.period = allPeriods.join(',');
      }
    } else if (selectedPeriods.length > 0) {
      params.period = selectedPeriods.map((p) => String(p.id).replace(/^P/i, '')).join(',');
    }

    if (selectedRouteTypes.length > 0) params.routeType = selectedRouteTypes.map((r) => String(r.id)).join(',');

    // Delivery Date range
    if (deliveryDateRange.start) params.startDate = deliveryDateRange.start;
    if (deliveryDateRange.end) params.endDate = deliveryDateRange.end;

    return params;
  }, [
    selectedRegions,
    selectedMarkets,
    selectedLocations,
    selectedYears,
    selectedPeriods,
    selectedWeeks,
    selectedRouteTypes,
    deliveryDateRange,
    locationOptions,
  ]);

  const currentKpiParams = useMemo<PerfectTruckKpiParams>(() => ({
    ...currentFilterParams,
    locationHierarchy: chartHierarchyLevel,
    timeframe: chartTimeframeGrain,
  }), [chartHierarchyLevel, chartTimeframeGrain, currentFilterParams]);

  // Compare normalized params to determine whether Apply should be enabled
  const isApplyEnabled = useMemo(() => {
    const normalize = (p: PerfectTruckKpiParams) => {
      const out: { [k: string]: string[] } = {};
      Object.keys(p).forEach((k) => {
        const v = (p as any)[k];
        if (v == null || v === '') return;
        // split comma lists, trim and sort for canonical form
        out[k] = String(v)
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== '')
          .sort();
      });
      return out;
    };

    const a = normalize(currentKpiParams);
    const b = normalize(manualBaselineParams || {});

    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    if (aKeys.length !== bKeys.length) return true;
    for (let i = 0; i < aKeys.length; i++) {
      if (aKeys[i] !== bKeys[i]) return true;
      const ak = a[aKeys[i]];
      const bk = b[bKeys[i]];
      if (ak.length !== bk.length) return true;
      for (let j = 0; j < ak.length; j++) if (ak[j] !== bk[j]) return true;
    }
    return false;
  }, [currentKpiParams, manualBaselineParams]);

  const tabs = useMemo(
    () => [
      { key: 'executive-overview' as const, label: 'Executive Overview' },
      { key: 'location-summary' as const, label: 'Location Summary' },
    ],
    []
  );

  // ─── KPI fetch ──────────────────────────────────────────────────────────
  const loadKpi = useCallback(async (params: PerfectTruckKpiParams = {}) => {
    setKpiLoading(true);
    try {
      const data = await fetchPerfectTruckKpi(params);
      setKpiData(data);
    } catch (err: any) {
      // Clear stale data so old results are not misleadingly shown
      setKpiData(null);
      const msg = err?.response?.data?.message
        || err?.message
        || 'An unexpected error occurred while fetching KPI data.';
      showApiError('Perfect Truck KPI Failed', msg);
    } finally {
      setKpiLoading(false);
    }
  }, []);

  // buildKpiParams is intentionally removed — handleApplyFilters and
  // handleResetFilters use currentKpiParams (the useMemo above) directly
  // so there is a single source of truth for parameter construction.

  // Apply filters — trigger KPI fetch with current selections
  const handleApplyFilters = () => {
    let filtersToApply: PerfectTruckKpiParams = { ...currentFilterParams };

    // If the selected delivery-date range is outside the selected Year/Period/Week
    // scope, Year/Period/Week are cleared on Apply and delivery-date is kept.
    if (isDeliveryDateOutsideConstrainedScope) {
      setSelectedYears([]);
      setSelectedPeriods([]);
      setSelectedWeeks([]);
      setActiveQuickSelect(null);
      const { year, period, week, ...rest } = filtersToApply;
      filtersToApply = rest;
    }

    const kpiParamsToApply: PerfectTruckKpiParams = {
      ...filtersToApply,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    };

    // use the precomputed canonical params so toggling back to the
    // previously-applied state will correctly disable the Apply button
    setAppliedFilterParams(filtersToApply);
    setManualBaselineParams(kpiParamsToApply);
    loadKpi(kpiParamsToApply);
  };

  const handleQuickSelectPress = (key: QuickSelectKey) => {
    if (activeQuickSelect === key) {
      const clearedParams: PerfectTruckKpiParams = { ...currentFilterParams };
      delete clearedParams.year;
      delete clearedParams.period;
      delete clearedParams.week;
      delete clearedParams.startDate;
      delete clearedParams.endDate;

      const appliedParams: PerfectTruckKpiParams = {
        ...clearedParams,
        locationHierarchy: chartHierarchyLevel,
        timeframe: chartTimeframeGrain,
      };

      setSelectedYears([]);
      setSelectedPeriods([]);
      setSelectedWeeks([]);
      setDeliveryDateRange({ start: null, end: null });
      setActiveQuickSelect(null);
      setAppliedFilterParams(clearedParams);
      setManualBaselineParams(appliedParams);
      loadKpi(appliedParams);
      return;
    }

    applyQuickSelect(key);
  };

  // ─── Time Frame quick-select (Week-to-Date / Period-to-Date / Quarter-to-Date /
  // Year-to-Date / Last Closed Week / Last Closed Period) ──────────────────────
  // Builds Year/Period/Week/Delivery Date selections from the current period-week
  // (redux) and applies them immediately. Only one quick-select is active
  // at a time; manually adjusting Year/Period/Week/Delivery Date deselects it
  // (see handleYearChange / handlePeriodChange / handleWeekChange /
  // handleDeliveryDateChange above).
  const buildYearItem = (year: number): KPIFilterDropdownItem => ({ id: String(year), displayText: String(year) });
  const buildPeriodItems = (periods: number[]): KPIFilterDropdownItem[] =>
    periods.map((p) => ({ id: `P${p}`, displayText: `P${p}` }));
  const buildWeekItems = (entries: { period: number; weekInPeriod: number }[]): KPIFilterDropdownItem[] =>
    entries.map(({ period, weekInPeriod }) => {
      const weekInYear = (period - 1) * WEEKS_PER_PERIOD + weekInPeriod;
      return {
        id: `P${period}W${weekInPeriod}`,
        displayText: `W${weekInYear} (P${period}W${weekInPeriod})`,
        weekInYearNum: weekInYear,
      };
    });

  const applyQuickSelect = (key: QuickSelectKey) => {
    if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return;
    const { year, period, week } = currentPeriodWeek;

    let targetYear = year;
    let periods: number[] = [period];
    // weekEntries: { period, weekInPeriod }[] to select
    let weekEntries: { period: number; weekInPeriod: number }[] = [];
    // "-to-Date" pills (WTD/PTD/QTD/YTD) end on today's actual date; Last Closed
    // Week/Period are fully-closed ranges and end at the close of that week/period.
    let endsToday = true;

    switch (key) {
      case 'wtd': {
        // Week-to-Date: current period, current week only
        weekEntries = [{ period, weekInPeriod: week }];
        break;
      }
      case 'ptd': {
        // Period-to-Date: current period, weeks 1..current week
        for (let w = 1; w <= week; w++) weekEntries.push({ period, weekInPeriod: w });
        break;
      }
      case 'qtd': {
        // Quarter-to-Date: periods from quarter start through current period,
        // all weeks for prior periods, weeks 1..current week for current period
        const quarterStart = [...QUARTER_START_PERIODS].reverse().find((qs) => qs <= period) ?? 1;
        periods = [];
        for (let p = quarterStart; p <= period; p++) {
          periods.push(p);
          const maxWeek = p === period ? week : WEEKS_PER_PERIOD;
          for (let w = 1; w <= maxWeek; w++) weekEntries.push({ period: p, weekInPeriod: w });
        }
        break;
      }
      case 'ytd': {
        // Year-to-Date: periods 1..current period
        periods = [];
        for (let p = 1; p <= period; p++) {
          periods.push(p);
          const maxWeek = p === period ? week : WEEKS_PER_PERIOD;
          for (let w = 1; w <= maxWeek; w++) weekEntries.push({ period: p, weekInPeriod: w });
        }
        break;
      }
      case 'lcw': {
        // Last Closed Week: the week immediately before the current week
        endsToday = false;
        let lcPeriod = period;
        let lcWeek = week - 1;
        if (lcWeek < 1) {
          lcPeriod = period - 1;
          lcWeek = WEEKS_PER_PERIOD;
          if (lcPeriod < 1) {
            targetYear = year - 1;
            lcPeriod = 13;
          }
        }
        periods = [lcPeriod];
        weekEntries = [{ period: lcPeriod, weekInPeriod: lcWeek }];
        break;
      }
      case 'lcp': {
        // Last Closed Period: the full period immediately before the current one
        endsToday = false;
        let lcPeriod = period - 1;
        if (lcPeriod < 1) {
          targetYear = year - 1;
          lcPeriod = 13;
        }
        periods = [lcPeriod];
        for (let w = 1; w <= WEEKS_PER_PERIOD; w++) weekEntries.push({ period: lcPeriod, weekInPeriod: w });
        break;
      }
      default:
        return;
    }

    const nextYears = [buildYearItem(targetYear)];
    const nextPeriods = buildPeriodItems(periods);
    const nextWeeks = buildWeekItems(weekEntries);

    const selectedRange = getScopedDateRangeFromPeriodWeeks([targetYear], periods, weekEntries);
    if (!selectedRange) return;

    // Derive the Delivery Date range from the actual API day rows so fiscal
    // boundaries like P1W1 starting in late 2025 stay aligned with PepsiCo's
    // calendar instead of being clipped to Jan 1.
    const startISO = selectedRange.start;
    const endISO = endsToday ? toISODate(new Date()) : selectedRange.end;

    setSelectedYears(nextYears);
    setSelectedPeriods(nextPeriods);
    setSelectedWeeks(nextWeeks);
    setDeliveryDateRange({ start: startISO, end: endISO });
    setActiveQuickSelect(key);

    const quickSelectParams: PerfectTruckKpiParams = {
      ...currentFilterParams,
      year: String(targetYear),
      period: periods.join(','),
      week: weekEntries.map(({ weekInPeriod }) => String(weekInPeriod)).join(','),
      startDate: startISO,
      endDate: endISO,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    };

    setAppliedFilterParams({
      ...currentFilterParams,
      year: String(targetYear),
      period: periods.join(','),
      week: weekEntries.map(({ weekInPeriod }) => String(weekInPeriod)).join(','),
      startDate: startISO,
      endDate: endISO,
    });
    setManualBaselineParams(quickSelectParams);
    loadKpi(quickSelectParams);
  };

  // ─── Fetch ALL route types on mount ──────────────────────────────────────
  // Active routes are listed first, then inactive routes, alphabetical within
  // each group. Inactive routes render muted with an italic "(Inactive)" tag
  // (via KPIFilterDropdown's isInactive support) but remain fully selectable.
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const rawTypes = await fetchAllRouteTypesWithStatus();
        if (!isMounted) return;
        const sorted = [...rawTypes].sort((a, b) => {
          if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
          return a.label.localeCompare(b.label);
        });
        setRouteTypeOptions(
          sorted.map((rt) => ({ id: rt.value, displayText: rt.label, isInactive: !rt.isActive })),
        );
      } catch {
        // leave empty on error
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  // ─── Load KPI on mount with the pre-selected location (if any) ──────────
  // When restoring a snapshot (returned via the Perfect Truck breadcrumb),
  // re-use its already-applied filter params instead of rebuilding from the
  // header location, so the report data matches what the user left behind.
  useEffect(() => {
    const initParams: PerfectTruckKpiParams = restoreSnapshot
      ? restoreSnapshot.appliedFilterParams ?? initialDefaultKpiParams
      : initialDefaultKpiParams;
    setAppliedFilterParams(initParams);
    setManualBaselineParams(
      restoreSnapshot?.manualBaselineParams ?? {
        ...initParams,
        locationHierarchy: chartHierarchyLevel,
        timeframe: chartTimeframeGrain,
      }
    );
    loadKpi({
      ...initParams,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    });
    initialKpiLoadCompleteRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialKpiLoadCompleteRef.current) return;
    // Keep chart config (hierarchy/timeframe) reactive, but always use the
    // last explicitly applied filters so draft filter edits do not auto-apply.
    loadKpi({
      ...appliedFilterParams,
      locationHierarchy: chartHierarchyLevel,
      timeframe: chartTimeframeGrain,
    });
  }, [appliedFilterParams, chartHierarchyLevel, chartTimeframeGrain, loadKpi]);

  // ─── Continuously mirror filter/tab state to Redux ──────────────────────
  // So that if the user navigates away to Perfect Truck (via a Location
  // Summary location link) and back via the "Perfect Truck Analytics"
  // breadcrumb, the filters and active tab they left with can be restored.
  useEffect(() => {
    dispatch(
      saveSnapshot({
        activeTab,
        selectedRegions,
        selectedMarkets,
        selectedLocations,
        selectedYears,
        selectedPeriods,
        selectedWeeks,
        deliveryDateRange,
        activeQuickSelect,
        selectedRouteTypes,
        appliedFilterParams,
        manualBaselineParams,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    selectedRegions,
    selectedMarkets,
    selectedLocations,
    selectedYears,
    selectedPeriods,
    selectedWeeks,
    deliveryDateRange,
    activeQuickSelect,
    selectedRouteTypes,
    appliedFilterParams,
    manualBaselineParams,
  ]);

  // ─── Chart Configuration bar (Executive Overview tab only) ──────────────
  const renderChartConfigurationBar = () => {
    if (activeTab !== 'executive-overview') return null;
    const chartConfigCountLabel = '2 / 2';
    return (
      <View style={styles.chartConfigWrapper}>
        <View style={styles.chartConfigCard}>
          <TouchableOpacity
            style={styles.chartConfigHeader}
            onPress={() => setIsChartConfigExpanded((prev) => !prev)}
            accessibilityRole="button"
            accessibilityLabel="Chart Configuration"
            testID="pta-chart-config-toggle"
          >
            <View style={styles.chartConfigTitleRow}>
              <MaterialIcons name="tune" size={18} color="#005CBC" />
              <Text style={styles.chartConfigLabel}>Chart Configuration</Text>
              <View style={styles.chartConfigCountPill}>
                <Text style={styles.chartConfigCountText}>{chartConfigCountLabel}</Text>
              </View>
              {Platform.OS === 'web' && (
                <Text style={styles.chartConfigDescription} numberOfLines={1}>
                  Grouping and timeframe for all charts on this tab
                </Text>
              )}
            </View>
            <View style={styles.chartConfigChevronWrap}>
              <MaterialIcons
                name={isChartConfigExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                size={24}
                color="#616161"
              />
            </View>
          </TouchableOpacity>

          {isChartConfigExpanded && (
            <View style={styles.chartConfigBody}>
              <View style={styles.chartConfigGroup}>
                <Text style={styles.chartConfigGroupLabel}>Location Hierarchy</Text>
                <View style={styles.segmentedControl}>
                  {CHART_HIERARCHY_OPTIONS.map((opt, idx) => {
                    const isActive = chartHierarchyLevel === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        style={[
                          styles.segmentedOption,
                          idx > 0 && styles.segmentedOptionDivider,
                          isActive && styles.segmentedOptionActive,
                        ]}
                        onPress={() => setChartHierarchyLevel(opt.key)}
                        accessibilityRole="button"
                        accessibilityLabel={opt.label}
                        testID={`pta-chart-hierarchy-${opt.key}`}
                      >
                        <Text style={[styles.segmentedOptionText, isActive && styles.segmentedOptionTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              <View style={styles.chartConfigGroup}>
                <Text style={styles.chartConfigGroupLabel}>Timeframe</Text>
                <View style={styles.segmentedControl}>
                  {CHART_TIMEFRAME_OPTIONS.map((opt, idx) => {
                    const isActive = chartTimeframeGrain === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        style={[
                          styles.segmentedOption,
                          idx > 0 && styles.segmentedOptionDivider,
                          isActive && styles.segmentedOptionActive,
                        ]}
                        onPress={() => setChartTimeframeGrain(opt.key)}
                        accessibilityRole="button"
                        accessibilityLabel={opt.label}
                        testID={`pta-chart-timeframe-${opt.key}`}
                      >
                        <Text style={[styles.segmentedOptionText, isActive && styles.segmentedOptionTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    );
  };

  if (isMobileIos && isMobileFilterOverlayVisible) {
    return (
      <View style={styles.mobileOverlayContainer}>
        <ScrollView
          style={styles.mobileOverlayBody}
          contentContainerStyle={styles.mobileOverlayBodyContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>REGION / UNIT</Text>
            <KPIFilterDropdown
              childList={regionOptions}
              selectedValue={selectedRegions}
              setSelectedValue={handleRegionChange}
              placeholder="All"
              isOpen={openFilterKey === 'region'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)}
              testID="pta-mobile-filter-region"
              autoFocusSearchOnOpen
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>MARKET / POD</Text>
            <KPIFilterDropdown
              childList={marketOptions}
              selectedValue={selectedMarkets}
              setSelectedValue={handleMarketChange}
              placeholder="All"
              isOpen={openFilterKey === 'market'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)}
              testID="pta-mobile-filter-market"
              autoFocusSearchOnOpen
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>LOCATION</Text>
            <KPIFilterDropdown
              childList={locationOptions}
              selectedValue={selectedLocations}
              setSelectedValue={setSelectedLocations}
              placeholder="All"
              isOpen={openFilterKey === 'location'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'location' : null)}
              testID="pta-mobile-filter-location"
              autoFocusSearchOnOpen
              sortItems
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>YEAR</Text>
            <KPIFilterDropdown
              childList={yearOptions}
              selectedValue={selectedYears}
              setSelectedValue={handleYearChange}
              placeholder="All"
              isOpen={openFilterKey === 'year'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)}
              testID="pta-mobile-filter-year"
              autoFocusSearchOnOpen
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>PERIOD</Text>
            <KPIFilterDropdown
              childList={periodOptions}
              selectedValue={selectedPeriods}
              setSelectedValue={handlePeriodChange}
              placeholder="All"
              isOpen={openFilterKey === 'period'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)}
              testID="pta-mobile-filter-period"
              autoFocusSearchOnOpen
              disabledIds={disabledPeriodIds}
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>WEEK IN YEAR</Text>
            <KPIFilterDropdown
              childList={weekOptions}
              selectedValue={selectedWeeks}
              setSelectedValue={handleWeekChange}
              placeholder="All"
              isOpen={openFilterKey === 'week'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)}
              testID="pta-mobile-filter-week"
              autoFocusSearchOnOpen
              disabledIds={disabledWeekIds}
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <View style={styles.filterLabelRow}>
              <Text style={[styles.mobileOverlayFilterLabel, styles.filterLabelInRow]}>DELIVERY DATE</Text>
              {deliveryDateDisabledReason ? (
                <Text style={styles.filterHintText}>{deliveryDateDisabledReason}</Text>
              ) : null}
            </View>
            <DeliveryDateRangeFilter
              value={deliveryDateRange}
              onChange={handleDeliveryDateChange}
              minDate={constrainedDeliveryDateScope?.start}
              maxDate={constrainedDeliveryDateScope?.end}
              disabled={!!deliveryDateDisabledReason}
              isOpen={openFilterKey === 'deliveryDate'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'deliveryDate' : null)}
              testID="pta-mobile-filter-delivery-date"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>TIMEFRAME</Text>
            <View style={styles.quickSelectRow}>
              {QUICK_SELECT_OPTIONS.map((opt) => {
                const isActive = activeQuickSelect === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.quickSelectPill, isActive && styles.quickSelectPillActive]}
                    onPress={() => handleQuickSelectPress(opt.key)}
                    accessibilityRole="button"
                    accessibilityLabel={opt.label}
                    testID={`pta-mobile-quick-select-${opt.key}`}
                  >
                    <Text style={[styles.quickSelectPillText, isActive && styles.quickSelectPillTextActive]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Chart Configuration — mobile: within this Filters/Charts panel, below the filter bar */}
          {renderChartConfigurationBar()}
        </ScrollView>

        <View style={styles.mobileOverlayFooter}>
          <TouchableOpacity
            style={styles.mobileOverlayCancelBtn}
            onPress={() => setMobileFilterOverlayVisible(false)}
            accessibilityRole="button"
            accessibilityLabel="Close filters"
          >
            <Text style={styles.mobileOverlayCancelBtnText}>Close</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.mobileOverlayResetBtn}
            onPress={() => { handleResetFilters(); setMobileFilterOverlayVisible(false); }}
            accessibilityRole="button"
            accessibilityLabel="Reset filters"
            testID="pta-filter-reset"
          >
            <Text style={styles.mobileOverlayResetBtnText}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.mobileOverlayApplyBtn}
            onPress={() => { handleApplyFilters(); setMobileFilterOverlayVisible(false); }}
            accessibilityRole="button"
            accessibilityLabel="Apply filters"
            testID="pta-filter-apply"
          >
            <Text style={styles.mobileOverlayApplyBtnText}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator
    >
      {/* Breadcrumb */}
      <View style={{ marginHorizontal: 24, marginTop: 12 }}>
        <DLOSimpleBreadcrumb
          path={["Analytics Hub", title]}
          onHomePress={() => {
            // Prefer in-app navigation helper when available to avoid full reloads
            const nav = (globalThis as any)?.appNavigate;
            if (typeof nav === 'function') {
              nav('Home');
              return;
            }

            // Fallback: web history replace then dispatch popstate
            if (Platform.OS === 'web') {
              try {
                window.history.pushState({}, '', '/home');
                window.dispatchEvent(new PopStateEvent('popstate'));
              } catch {
                window.location.href = '/home';
              }
            } else {
              if (typeof window !== 'undefined') window.location.href = '/home';
            }
          }}
          onNavigate={(index, label) => {
            if (index === 0) {
              const nav = (globalThis as any)?.appNavigate;
              if (typeof nav === 'function') {
                nav('Analytics');
                return;
              }

              if (Platform.OS === 'web') {
                try {
                  window.history.pushState({}, '', '/analytics');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                } catch {
                  window.location.href = '/analytics';
                }
              }
            }
          }}
        />
      </View>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <MaterialIcons name={iconName as any} size={28} color="#005CBC" />
          <Text style={styles.title} numberOfLines={1} adjustsFontSizeToFit={false}>
            {title}
          </Text>
        </View>
        {/* On web (non-iOS mobile) keep badge at right; on iOS mobile we render it below the title */}
        {!isMobileIos && !!lastRefreshedLabel && (
          <View style={styles.refreshedBadge}>
            <Text style={styles.refreshedBadgeText}>
              {'Refreshed: '}
              <Text style={styles.refreshedBadgeValueText}>{lastRefreshedLabel}</Text>
            </Text>
          </View>
        )}
      </View>

      {/* iOS mobile: render refreshed label below the title so it's always stacked */}
      {isMobileIos && !!lastRefreshedLabel && (
        <View style={styles.refreshedBadgeMobileContainer}>
          <Text style={styles.refreshedBadgeText}>
            {'Refreshed: '}
            <Text style={styles.refreshedBadgeValueText}>{lastRefreshedLabel}</Text>
          </Text>
        </View>
      )}

      {/* Mobile Filter button — shown above the tabs, matching Analytics Hub placement */}
      {isMobileIos && (
        <TouchableOpacity
          style={styles.mobileFilterBtn}
          onPress={() => setMobileFilterOverlayVisible(true)}
          accessibilityRole="button"
          accessibilityLabel={`Filters, ${mobileActiveFilterCount} active`}
        >
          <MaterialIcons name="filter-list" size={18} color="#2D2D2D" />
          <Text style={styles.mobileFilterBtnText}>Filters</Text>
          {mobileActiveFilterCount > 0 && (
            <View style={styles.mobileFilterBadge}>
              <Text style={styles.mobileFilterBadgeText}>{mobileActiveFilterCount}</Text>
            </View>
          )}
        </TouchableOpacity>
      )}

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabBar}
        contentContainerStyle={styles.tabBarContent}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, isActive && styles.tabActive]}
              onPress={() => setActiveTab(tab.key)}
              activeOpacity={0.7}
            >
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Filter Bar */}
      <View style={[styles.filterBarWrapper, isMobileIos && styles.filterBarWrapperMobileHidden]}>
        <View style={styles.filterBar}>
          <View style={styles.filtersRow}>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>REGION / UNIT</Text>
              <KPIFilterDropdown
                childList={regionOptions}
                selectedValue={selectedRegions}
                setSelectedValue={handleRegionChange}
                placeholder="All"
                isOpen={openFilterKey === 'region'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)}
                testID="pta-filter-region"
              />
            </View>

            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>MARKET / POD</Text>
              <KPIFilterDropdown
                childList={marketOptions}
                selectedValue={selectedMarkets}
                setSelectedValue={handleMarketChange}
                placeholder="All"
                isOpen={openFilterKey === 'market'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)}
                testID="pta-filter-market"
              />
            </View>

            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>LOCATION</Text>
              <KPIFilterDropdown
                childList={locationOptions}
                selectedValue={selectedLocations}
                setSelectedValue={setSelectedLocations}
                placeholder="All"
                isOpen={openFilterKey === 'location'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'location' : null)}
                testID="pta-filter-location"
              />
            </View>

            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>YEAR</Text>
              <KPIFilterDropdown
                childList={yearOptions}
                selectedValue={selectedYears}
                setSelectedValue={handleYearChange}
                placeholder="All"
                isOpen={openFilterKey === 'year'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)}
                testID="pta-filter-year"
              />
            </View>

            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>PERIOD</Text>
              <KPIFilterDropdown
                childList={periodOptions}
                selectedValue={selectedPeriods}
                setSelectedValue={handlePeriodChange}
                placeholder="All"
                isOpen={openFilterKey === 'period'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)}
                testID="pta-filter-period"
                disabledIds={disabledPeriodIds}
              />
            </View>

            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>WEEK IN YEAR</Text>
              <KPIFilterDropdown
                childList={weekOptions}
                selectedValue={selectedWeeks}
                setSelectedValue={handleWeekChange}
                placeholder="All"
                isOpen={openFilterKey === 'week'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)}
                testID="pta-filter-week"
                disabledIds={disabledWeekIds}
              />
            </View>

            <View style={[styles.filterItem, styles.deliveryDateFilterItem]}>
              <View style={styles.filterLabelRow}>
                <Text style={[styles.filterLabel, styles.filterLabelInRow]}>DELIVERY DATE</Text>
                {deliveryDateDisabledReason ? (
                  <Text style={styles.filterHintText}>{deliveryDateDisabledReason}</Text>
                ) : null}
              </View>
              <DeliveryDateRangeFilter
                value={deliveryDateRange}
                onChange={handleDeliveryDateChange}
                minDate={constrainedDeliveryDateScope?.start}
                maxDate={constrainedDeliveryDateScope?.end}
                disabled={!!deliveryDateDisabledReason}
                isOpen={openFilterKey === 'deliveryDate'}
                onOpenChange={(open) => setOpenFilterKey(open ? 'deliveryDate' : null)}
                testID="pta-filter-delivery-date"
              />
            </View>

            {/* Reset & Apply buttons */}
            <View style={styles.filterActions}>
              <TouchableOpacity style={styles.resetBtn} onPress={handleResetFilters} testID="pta-filter-reset">
                <Text style={styles.resetBtnText}>Reset</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.applyBtn, isApplyEnabled ? styles.applyBtnEnabled : styles.applyBtnDisabled]}
                onPress={handleApplyFilters}
                disabled={!isApplyEnabled}
                testID="pta-filter-apply"
              >
                <Text style={[styles.applyBtnText, isApplyEnabled && styles.applyBtnTextEnabled]}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.filterDivider} />

          {/* Timeframe quick-select — applies immediately and can be toggled off */}
          <View style={styles.timeframeRow}>
            <Text style={styles.timeframeLabel}>TIMEFRAME</Text>
            <View style={styles.quickSelectRow}>
              {QUICK_SELECT_OPTIONS.map((opt) => {
                const isActive = activeQuickSelect === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.quickSelectPill, isActive && styles.quickSelectPillActive]}
                    onPress={() => handleQuickSelectPress(opt.key)}
                    accessibilityRole="button"
                    accessibilityLabel={opt.label}
                    testID={`pta-quick-select-${opt.key}`}
                  >
                    <Text style={[styles.quickSelectPillText, isActive && styles.quickSelectPillTextActive]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </View>

      {/* Chart Configuration — desktop: below the global filter bar, Executive Overview tab only */}
      <View style={{ paddingHorizontal: 24 }}>
        {renderChartConfigurationBar()}
      </View>

      <View style={styles.tabContentContainer}>
        {activeTab === 'executive-overview' && (
          <ExecutiveOverview kpiData={kpiData} loading={kpiLoading} />
        )}
        {activeTab === 'location-summary' && <LocationSummary filterParams={appliedFilterParams} />}
      </View>

      {/* API Error Modal */}
      <DLOModal
        visible={apiErrorModal.visible}
        onClose={dismissApiError}
        title={apiErrorModal.title}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={dismissApiError}
        hideButtons={false}
        showCloseIcon
      >
        <Text style={styles.errorModalMessage}>{apiErrorModal.message}</Text>
      </DLOModal>
    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f9ff',
  },
  contentContainer: {
    paddingBottom: 40,
  },
  mobileFilterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D0D9E8',
    backgroundColor: '#FFFFFF',
    marginHorizontal: 24,
    marginTop: 12,
    marginBottom: 12,
  },
  mobileFilterBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: '#2D2D2D',
  },
  mobileFilterBadge: {
    backgroundColor: '#005CBC',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  mobileFilterBadgeText: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
  },
  filterBarWrapper: {
    paddingHorizontal: 24,
    paddingTop: 4,
    paddingBottom: 16,
  },
  filterBarWrapperMobileHidden: {
    display: 'none',
  },
  chartConfigWrapper: {
    paddingBottom: 16,
  },
  chartConfigCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  chartConfigHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 12,
  },
  chartConfigChevronWrap: {
    width: 24,
    height: 24,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chartConfigChevron: {
    transform: [{ rotate: '0deg' }],
  },
  chartConfigChevronExpanded: {
    transform: [{ rotate: '180deg' }],
  },
  chartConfigTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  chartConfigLabel: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2D2D',
  },
  chartConfigDescription: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#838383',
    flexShrink: 1,
  },
  chartConfigCountPill: {
    backgroundColor: '#0B5FC6',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 14,
  },
  chartConfigCountText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#FFFFFF',
  },
  chartConfigBody: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    columnGap: 32,
    rowGap: 16,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    marginHorizontal: 16,
    paddingTop: 16,
  },
  chartConfigGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  chartConfigGroupLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
  },
  segmentedControl: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 8,
    overflow: 'hidden',
  },
  segmentedOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#F2F4F7',
  },
  segmentedOptionDivider: {
    borderLeftWidth: 1,
    borderLeftColor: '#D0D5DD',
  },
  segmentedOptionActive: {
    backgroundColor: '#0B5FC6',
  },
  segmentedOptionText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#495057',
  },
  segmentedOptionTextActive: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  filterBar: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    padding: 20,
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  filtersRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 10,
  },
  filterItem: {
    minWidth: 140,
    flexGrow: 1,
  },
  deliveryDateFilterItem: {
    minWidth: 260,
    maxWidth: 320,
    flexGrow: 1.2,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  filterLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 8,
    gap: 6,
  },
  filterHintText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#0B5FC6',
  },
  filterLabelInRow: {
    marginBottom: 0,
  },
  filterActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginLeft: 'auto',
    marginTop: 20,
  },
  quickSelectRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 0,
    flex: 1,
  },
  filterDivider: {
    height: 1,
    backgroundColor: '#E3E9F3',
    marginTop: 16,
    marginBottom: 16,
  },
  timeframeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 0,
  },
  timeframeLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
  },
  quickSelectPill: {
    borderWidth: 1,
    borderColor: '#E3E9F3',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
  },
  quickSelectPillActive: {
    backgroundColor: '#0B5FC6',
    borderColor: '#0B5FC6',
  },
  quickSelectPillText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#3D4147',
  },
  quickSelectPillTextActive: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  resetBtn: {
    borderWidth: 1,
    borderColor: '#0B5FC6',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
    minHeight: 40,
    justifyContent: 'center',
  },
  resetBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#0B5FC6',
  },
  applyBtn: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    minHeight: 40,
    justifyContent: 'center',
  },
  applyBtnEnabled: {
    backgroundColor: '#0B5FC6',
  },
  applyBtnDisabled: {
    backgroundColor: '#D0D5DD',
  },
  applyBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#9CA3AF',
  },
  applyBtnTextEnabled: {
    color: '#FFFFFF',
  },
  tabBar: {
    marginHorizontal: 24,
    marginTop: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
    paddingBottom: 0,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: '#005CBC',
  },
  tabLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: FontFamily.medium,
    fontWeight: '700',
  },
  tabLabelActive: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  tabContentContainer: {
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 10,
  },
  mobileOverlayContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  mobileOverlayBody: {
    flex: 1,
  },
  mobileOverlayBodyContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 24,
    rowGap: 14,
  },
  mobileOverlayFilterRow: {
    gap: 8,
  },
  mobileOverlayFilterLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#6A6D71',
    letterSpacing: 0.4,
    fontFamily: FontFamily.bold,
    marginBottom: 0,
  },
  mobileOverlayFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  mobileOverlayCancelBtn: {
    flex: 1,
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  mobileOverlayCancelBtnText: {
    fontSize: 14,
    color: '#344054',
    fontFamily: FontFamily.bold,
  },
  mobileOverlayResetBtn: {
    flex: 1,
    minHeight: 42,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#0B5FC6',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  mobileOverlayResetBtnText: {
    fontSize: 14,
    color: '#0B5FC6',
    fontFamily: FontFamily.bold,
  },
  mobileOverlayApplyBtn: {
    flex: 1,
    minHeight: 42,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0B5FC6',
  },
  mobileOverlayApplyBtnText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  refreshedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  refreshedBadgeText: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: FontFamily.regular,
    color: '#4B5563',
  },
  refreshedBadgeValueText: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#005CBC'
  },
  refreshedBadgeMobileContainer: {
    marginHorizontal: 24,
    marginTop: -10,
    alignItems: 'flex-start',
  },
  errorModalMessage: {
    fontSize: 16,
    color: '#3A3A3A',
    lineHeight: 24,
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: FontFamily.regular,
  },
});

export default PerfectTruckAnalyticsDashboard;
